import { useState } from 'react'
import './index.scss'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import CampusController from '@data/controller/CampusController'
import CategoryController from '@data/controller/CategoryController'
import { Campus, Category } from '@data/ChurchDataProvider'

import closeIcon from 'url:@icon/close.svg';
import warningIcon from 'url:@icon/error-icon.svg';

// Sanctuary's refusal when content still points at the campus or category.
const HTTP_CONFLICT = 409;

// Sanctuary refuses to delete a campus or category while anything still points
// at it, so this says so before the attempt, and turns into an explanation if
// the delete is refused.
export default function DeleteCampusModal(props: {
  isOpen: boolean,
  campus?: Campus | null,
  category?: Category | null,
  close: () => void,
  onDeleted: () => void
}) {
  const auth = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sanctuary's message naming what still uses it, e.g. "This campus is still
  // used by 2 playlists and 1 event. Remove or reassign that content first."
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const targetType = props.campus ? 'campus' : 'category';
  const targetName = props.campus?.attributes.name ?? props.category?.name ?? (targetType === 'campus' ? 'Campus' : 'Category');

  const deleteTarget = async () => {
    if (!auth.churchId || (!props.campus && !props.category)) {
      setError(`Missing ${targetType} context. Please refresh and try again.`);
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = props.campus
        ? await CampusController.deleteCampus(auth.churchId, props.campus.id, auth)
        : await CategoryController.deleteCategory(auth.churchId, props.category!.id, auth);

      if (response.status === HTTP_CONFLICT) {
        setBlockedReason(response.error ?? `This ${targetType} is still in use. Remove or reassign that content first.`);
        return;
      }

      if (response.error) {
        setError(response.error);
        return;
      }

      props.onDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return <Modal
    id='delete-campus-modal'
    isOpen={props.isOpen}
    footerButtons={blockedReason ? [
      {
        title: 'Done',
        style: ModalButtonStyle.OUTLINE,
        onClick: props.close
      }
    ] : [
      {
        title: targetType === 'campus' ? 'Delete Campus' : 'Delete Category',
        style: ModalButtonStyle.FILLED,
        enabled: !isDeleting,
        onClick: deleteTarget
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isDeleting,
        onClick: props.close
      }
    ]}>
    <div className='delete-campus-modal-content'>
      <div className='header'>
        <a aria-disabled={isDeleting}
          className={isDeleting ? 'close disabled' : 'close'}
          href='#'
          onClick={(e) => {
            e.preventDefault();

            if (isDeleting) {
              return;
            }

            props.close();
          }}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </a>

        <h3 className='title'>{blockedReason ? `Can't Delete ${targetName}` : `Delete ${targetName}`}</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <img src={warningIcon} alt='' aria-hidden='true' className='warning-icon' />

        {blockedReason ? <>
          <p className='warning-copy'>
            <strong>{targetName} can't be deleted while content still uses it.</strong>
          </p>

          <p className='warning-copy'>{blockedReason}</p>

          <p className='warning-copy caption'>
            Open each item and choose a different {targetType}, or none, then try deleting again.
          </p>
        </> : <>
          <p className='warning-copy'>
            <strong>Are you sure you want to delete {targetName}?</strong>
          </p>

          <p className='warning-copy'>
            A {targetType} can only be deleted once nothing is linked to it. If any Launchpads, Playlists, Services,
            or Events still use {targetName}, deletion won't be possible until they're unlinked from it.
          </p>

          <p className='warning-copy caption'>This action cannot be undone.</p>
        </>}
      </div>
    </div>
  </Modal>
}
